import Vue from 'vue';

const INITIAL_STATE = () => ({
  fetchError: null,
  sendErrors: {},
  messages: {},
  channels: [],
  subscriptions: {},
  connected: false,
  filters: {
    selectedSite: null,
    channelOwner: 'me', // Default to "Myself"
    sortBy: 'lastUnreadInbound' // Default to "Last Unread Inbound"
  }
});

export const state = INITIAL_STATE;

function sortChannels(channels, sortBy = 'lastUnreadInbound') {
  return channels.sort((a, b) => {
    // Always prioritize "AI" channel first
    if (a.isAI && !b.isAI) return -1;
    if (b.isAI && !a.isAI) return 1;

    switch (sortBy) {
      case 'lastUnreadInbound': {
        // Channels with unread inbound messages first, then by last message date
        const hasUnreadA = a.unreadMessages > 0;
        const hasUnreadB = b.unreadMessages > 0;
        
        if (hasUnreadA && !hasUnreadB) return -1;
        if (!hasUnreadA && hasUnreadB) return 1;
        
        // Both have unread or both don't have unread - sort by date
        const dateA = a.lastMessage?.lastModifiedDate || a.lastModifiedDate;
        const dateB = b.lastMessage?.lastModifiedDate || b.lastModifiedDate;
        return dateB.localeCompare(dateA);
      }
      
      case 'lastSent': {
        // Sort by last message date descending (newest first)
        const dateA = a.lastMessage?.lastModifiedDate || a.lastModifiedDate;
        const dateB = b.lastMessage?.lastModifiedDate || b.lastModifiedDate;
        return dateB.localeCompare(dateA);
      }
      
      case 'lastSent30Days': {
        // Filter channels where last message is >= 30 days old, then sort by date
        const now = new Date();
        const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
        
        const dateA = new Date(a.lastMessage?.lastModifiedDate || a.lastModifiedDate);
        const dateB = new Date(b.lastMessage?.lastModifiedDate || b.lastModifiedDate);
        
        const isOldA = dateA <= thirtyDaysAgo;
        const isOldB = dateB <= thirtyDaysAgo;
        
        // Prioritize channels >= 30 days old
        if (isOldA && !isOldB) return -1;
        if (!isOldA && isOldB) return 1;
        
        // Both old or both recent - sort by date descending
        return dateB.getTime() - dateA.getTime();
      }
      
      case 'lastUnreadOutbound': {
        // This is complex - we need to determine if there are unread outbound messages
        // For now, we'll show channels with unread messages first, sorted by date
        // Note: This may need refinement based on how "unread outbound" is tracked
        const hasUnreadA = a.unreadMessages > 0;
        const hasUnreadB = b.unreadMessages > 0;
        
        if (hasUnreadA && !hasUnreadB) return -1;
        if (!hasUnreadA && hasUnreadB) return 1;
        
        const dateA = a.lastMessage?.lastModifiedDate || a.lastModifiedDate;
        const dateB = b.lastMessage?.lastModifiedDate || b.lastModifiedDate;
        return dateB.localeCompare(dateA);
      }
      
      default: {
        // Default to lastSent behavior
        const dateA = a.lastMessage?.lastModifiedDate || a.lastModifiedDate;
        const dateB = b.lastMessage?.lastModifiedDate || b.lastModifiedDate;
        return dateB.localeCompare(dateA);
      }
    }
  });
}

export const mutations = {
  RESET (state) {
    Object.assign(state, INITIAL_STATE());
  },
  SET_FETCH_ERROR (state, error) {
    state.fetchError = error;
  },
  SET_SEND_ERROR (state, {principal, errorMessage}) {
    state.sendErrors = {
      ...state.sendErrors,
      [principal]: errorMessage,
    }
  },
  SET_MESSAGES (state, messages) {
    state.messages = messages;
  },
  SET_CHANNELS (state, channels) {
    state.channels = sortChannels(channels, state.filters.sortBy);
  },
  SET_CHANNEL (state, channel) {
    const channels = state.channels.filter((c) => c.id !== channel.id);
    state.channels = sortChannels([channel, ...channels], state.filters.sortBy);
  },
  SET_MESSAGES_FOR_CHANNEL (state, { channelId, messages, hasEarlierMessages=state.messages[channelId]?.hasEarlierMessages }) {
    Vue.set(state.messages, channelId, {messages, hasEarlierMessages});
  },
  ADD_MESSAGE (state, { message }) {
    const { channelId } = message;
    const { messages = [], hasEarlierMessages } = state.messages[channelId] || {};
    const i = messages.findIndex(m => m.id === message.id)
    if (i >= 0) {
      messages.splice(i, 1, message);
    } else {
      messages.unshift(message);
    }
    Vue.set(state.messages, channelId, { messages, hasEarlierMessages });
  },
  UPDATE_MESSAGE_STATUS (state, { messageId, channelId, status }) {
    if (!messageId || !status) return;

    const message = channelId
        // Search in specified channel
        ? state.messages[channelId]?.messages?.find(m => m.id === messageId)
        // Search across all channels
        : Object.values(state.messages).flatMap(c => c.messages).find(m => m.id === messageId);

    if (message) {
      message.inAppStatus = status;
      const channel = state.messages[message.channelId]
      Vue.set(state.messages, channelId, channel);
    }
  },
  SET_FILTERS (state, filters) {
    const oldSortBy = state.filters.sortBy;
    state.filters = { ...state.filters, ...filters };
    
    // Re-sort channels if sortBy changed
    if (filters.sortBy && filters.sortBy !== oldSortBy) {
      state.channels = sortChannels([...state.channels], state.filters.sortBy);
    }
  },
  UPDATE_MESSAGE_STATUS_BY_DATE (state, { userId, principal, date, status }) {
    if (!userId || !principal || !date || !status) return;

    const channel = state.channels.find(c => c.principal === principal);
    if (!channel) return;

    const { messages, hasEarlierMessages } = state.messages[channel.id];
    if (!messages) return;

    let changed = false;
    messages.forEach(m => {
      if (
          m.createdDate <= date &&
          m.sender !== channel.patientFullName &&
          m.inAppStatus !== status
      ) {
        m.inAppStatus = status;
        changed = true;
      }
    })
    if (changed) Vue.set(state.messages, channel.id, { messages, hasEarlierMessages });
  },
  ADD_SUBSCRIPTION (state, { principal, unsubscribe }) {
    state.subscriptions[principal] = unsubscribe;
  },
  REMOVE_SUBSCRIPTION (state, { principal }) {
    delete state.subscriptions[principal];
  },
  CONNECTED (state) {
    state.connected = true
  },
  DISCONNECTED (state) {
    state.connected = false
  },
};

export const actions = {
  initMessagingModule ({ commit, dispatch, state }) {
    this.$chat.onConnect(async () => {
      console.log('Chat: connected');
      // Subscribe for channels updates
      this.$chat.onChannelUpdated((channel) => commit('SET_CHANNEL', channel));

      // Query channels list
      await this.$chat.getPrioritizedChannels()
          .then(channels => {
            commit('SET_CHANNELS', channels);
          })
          .catch(error => {
            console.error('Messaging Store: getPrioritizedChannels() error:', error);
            commit("snackbar/error", `Couldn't get channels list (${error.message})`, { root: true });
          });

      // Restore subscriptions after reconnect
      for (const principal in state.subscriptions) {
        dispatch('subscribeToChannel', principal);
      }

      commit('CONNECTED');
    })

    this.$chat.onDisconnect(() => {
      console.log('Chat: disconnected');
      commit('DISCONNECTED');
    });

    this.$chat.activate();
  },

  teardownMessagingModule ({ commit }) {
    this.$chat.deactivate();
    commit('RESET');
  },

  async fetchMessagesForChannel ( { state, commit, getters }, { channelId, additional }) {
    if (!channelId) {
      throw new Error('Channel id required');
    }
    try {
      const existingMessages = getters.getMessagesByChannelId(channelId);
      const earliestCreatedDate = additional ? getters.earliestCreatedByChannelId(channelId) : undefined;

      const { messages: newMessages, hasEarlierMessages } = await this.$chat.getMessages(channelId, earliestCreatedDate)
      const messages = additional ? [...existingMessages, ...newMessages] : newMessages;

      commit('SET_MESSAGES_FOR_CHANNEL', { channelId, messages, hasEarlierMessages });
      commit('SET_FETCH_ERROR', null);

      return newMessages;
    } catch (err) {
      commit('SET_FETCH_ERROR', { message: 'Error retrieving messages' });
    }
  },

  async unsubscribeFromChannel ({ state, commit }, principal) {
    const unsubscribe = state.subscriptions[principal];
    if (unsubscribe) {
      commit('REMOVE_SUBSCRIPTION', { principal });
      unsubscribe();
    }
  },

  async subscribeToChannel ({ state, commit, dispatch }, principal) {
    const channel = state.channels.find(c => c.principal === principal);
    const { unsubscribe } = this.$chat.onChannelMessage(
        principal,
        (message) => {
          // Add message and mark it as read
          commit('ADD_MESSAGE', { message });
          dispatch('readMessage', { message });
        },
        (status) => commit('UPDATE_MESSAGE_STATUS', { ...status }),
        (status) => commit('UPDATE_MESSAGE_STATUS_BY_DATE', { ...status })
    );
    commit('ADD_SUBSCRIPTION', { principal, unsubscribe });
    if (channel) dispatch('fetchMessagesForChannel', { channelId: channel.id, additional: false });
    return channel;
  },

  async sendMessage ({ commit, dispatch}, {principal, message, medium}) {
    try {
      const sentMessage = await this.$chat.sendMessage(medium, principal, message)
      commit('SET_SEND_ERROR', {principal, errorMessage: null});
      return sentMessage;
    } catch (err) {
      if (err.code === 'UniqueConstraintChannelPhoneNumbers') {
        commit('SET_SEND_ERROR', {principal, errorMessage: 'This phone number is already being used by another patient.'})
      } else {
        console.log('Error sending message', err)
        commit('SET_SEND_ERROR', {principal, errorMessage: 'Error sending message' });
      }
    }
  },

  async readMessage ({ commit, dispatch}, {message}) {
    try {
      const updatedChannel = await this.$chat.readMessage(message.id)
      commit('SET_FETCH_ERROR', null);
      commit('SET_CHANNEL', updatedChannel)
      return updatedChannel;
    } catch (err) {
      console.log('Error marking message as read', err)
      commit('snackbar/error', `Couldn't mark message as read: ${err.message}`, { root: true });
    }
  },

  async unreadMessage ({ commit, dispatch}, {message}) {
    try {
      const updatedChannel = await this.$chat.unreadMessage(message.id)
      commit('SET_FETCH_ERROR', null);
      commit('SET_CHANNEL', updatedChannel)
      return updatedChannel;
    } catch (err) {
      console.error('Store: Error marking message as unread', err)
      console.error('Store: Error details:', err.message, err.stack);
      commit('SET_FETCH_ERROR', {message: 'Error marking message as unread' });
      commit('snackbar/error', `Couldn't mark message as unread: ${err.message}`, { root: true });
      throw err; // Re-throw so the component can catch it
    }
  },

  async watchChannel ({ commit, dispatch}, {channel}) {
    try {
      const updatedChannel = await this.$chat.watchChannel(channel.id)
      commit('SET_FETCH_ERROR', null);
      commit('SET_CHANNEL', updatedChannel)
      
      // Do NOT fetch messages here - just watch the channel without marking as read
      return updatedChannel;
    } catch (err) {
      console.log('Error subscribing to channel', err)
      commit('snackbar/error', `Subscribe error: ${err.message}`, { root: true });
    }
  },

  async unwatchChannel ({ commit, dispatch}, {channel, message}) {
    try {
      const updatedChannel = await this.$chat.unwatchChannel(channel.id, message)
      commit('SET_CHANNEL', updatedChannel)
      return updatedChannel;
    } catch (err) {
      console.log('Error unsubscribing to channel', err)
      commit('SET_FETCH_ERROR', {message: 'Error unsubscribing to channel' });
      commit('snackbar/error', `Unsubscribe error: ${err.message}`, { root: true });
    }
  },

  async inviteToChannel ({ commit, dispatch}, {channel, userId, message}) {
    try {
      const updatedChannel = await this.$chat.inviteToChannel(channel.id, userId, message)
      commit('SET_CHANNEL', updatedChannel)
      return updatedChannel;
    } catch (err) {
      console.log('Error inviting to channel', err)
      commit('snackbar/error', `Error inviting to channel: ${err.message}`, { root: true });
    }
  },

  async inviteToChannelByEmail ({ commit, dispatch}, {channel, email, message}) {
    try {
      const updatedChannel = await this.$chat.inviteToChannelByEmail(channel.id, email, message)
      commit('SET_CHANNEL', updatedChannel)
      return updatedChannel;
    } catch (err) {
      console.log('Error inviting to channel', err)
      commit('snackbar/error', `Error inviting to channel: ${err.message}`, { root: true });
    }
  }
};

export const getters = {
  getMessagesByChannelId: (state) => (channelId) => {
    return state.messages[channelId] && state.messages[channelId].messages || [];
  },
  earliestCreatedByChannelId: (_, getters) => (channelId) => {
    const channelMessages = getters.getMessagesByChannelId(channelId);
    return channelMessages.length ? channelMessages[channelMessages.length-1].createdDate : null;
  },
  hasEarlierMessagesByChannelId: (state) => (channelId) => {
    return state.messages[channelId] && state.messages[channelId].hasEarlierMessages;
  },
  unreadChannelsCount: (state, _, rootState, rootGetters) => {
    const ownerId = rootGetters['portal/userId'];
    return state.channels
        .filter(c => c.owner === ownerId || c.participantsMap.has(ownerId))
        .filter(c => c.unreadMessages > 0)
        .length
  },
  isConnected: (state) => state.connected,
  isDisconnected: (state) => !state.connected,
};
